// components/editorial/EditorialHeader.tsx
// ─── Kursi site header — modern SaaS nav ────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, useWindowDimensions } from 'react-native';
import { useRouter, usePathname } from 'expo-router';
import { Menu, X, LogOut } from 'lucide-react-native';
import { INK, PAPER, PAPER_SOFT, ACCENT, ACCENT_SOFT, ACCENT_DEEP, INK_MUTED, INK_SOFT } from '@/constants/theme';
import { theme } from '@/constants/theme';
import { supabase } from '@/utils/supabase';
import { AuthPopup } from '@/components/AuthPopup';

const NAV_LINKS = [
  { label: 'הקטלוג',     href: '/catalog' },
  { label: 'תחומים',     href: '/fields' },
  { label: 'איך זה עובד', href: '/how-it-works' },
  { label: 'שאלון',      href: '/quiz' },
  { label: 'מדריכים',    href: '/guides' },
  { label: 'אודות',      href: '/about' },
] as const;

const SANS_MED   = theme.fonts.medium;
const SANS_BOLD  = theme.fonts.bold;
const SANS_BLACK = theme.fonts.serifBlack;

const BREAKPOINT = 900;
const IS_WEB     = Platform.OS === 'web';

export function EditorialHeader({ compact = false }: { compact?: boolean }) {
  const router    = useRouter();
  const pathname  = usePathname();
  const { width } = useWindowDimensions();
  const isDesktop = width >= BREAKPOINT;
  const [menuOpen, setMenuOpen] = useState(false);

  const [authUser,     setAuthUser]     = useState<{ email: string; name?: string } | null>(null);
  const [authLoading,  setAuthLoading]  = useState(true);
  const [showAuthPopup, setShowAuthPopup] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user?.email) setAuthUser({ email: user.email, name: user.user_metadata?.full_name || user.user_metadata?.name });
      setAuthLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      const u = session?.user;
      if (u?.email) setAuthUser({ email: u.email, name: u.user_metadata?.full_name || u.user_metadata?.name });
      else setAuthUser(null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const handleSignOut = useCallback(async () => {
    await supabase.auth.signOut();
    setAuthUser(null);
    router.replace('/');
  }, [router]);

  const tapCountRef = useRef(0);
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleLogoPress = useCallback(() => {
    tapCountRef.current += 1;
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    tapTimerRef.current = setTimeout(() => { tapCountRef.current = 0; }, 600);
    if (tapCountRef.current >= 5) {
      tapCountRef.current = 0;
      router.push('/admin' as any);
    } else {
      router.push('/');
    }
  }, [router]);

  const navigate = useCallback((href: string) => {
    setMenuOpen(false);
    router.push(href as any);
  }, [router]);

  const isActive = (href: string) => pathname === href;

  // ─── Desktop ──────────────────────────────────────────────────────────────
  if (isDesktop) {
    return (
      <>
        <View style={styles.deskRoot} accessibilityRole={'banner' as any}>
          <View style={styles.deskInner}>
            {/* Logo */}
            <Pressable onPress={handleLogoPress} style={styles.logoBtn} accessibilityRole="link" accessibilityLabel="Kursi — חזרה לדף הבית">
              <Text style={styles.logoText}>Kursi</Text>
            </Pressable>

            {/* Nav links */}
            <View style={styles.deskNav}>
              {NAV_LINKS.map(({ label, href }) => (
                <Pressable
                  key={href}
                  onPress={() => navigate(href)}
                  style={({ hovered }: any) => [
                    styles.deskNavLink,
                    hovered && styles.deskNavLinkHover,
                  ]}
                  accessibilityRole="link"
                  accessibilityLabel={label}
                >
                  <Text style={[styles.deskNavLabel, isActive(href) && styles.deskNavLabelActive]}>
                    {label}
                  </Text>
                  {isActive(href) && <View style={styles.deskNavActiveDot} />}
                </Pressable>
              ))}
            </View>

            {/* Right actions */}
            <View style={styles.deskActions}>
              {!authLoading && (
                authUser ? (
                  <View style={styles.authRow}>
                    <View style={styles.avatar}>
                      <Text style={styles.avatarText}>
                        {(authUser.name?.[0] || authUser.email[0] || '?').toUpperCase()}
                      </Text>
                    </View>
                    <Pressable onPress={handleSignOut} hitSlop={8} style={styles.signOutBtn}>
                      <LogOut size={14} color={INK_MUTED} />
                    </Pressable>
                  </View>
                ) : (
                  <Pressable
                    onPress={() => setShowAuthPopup(true)}
                    style={({ hovered }: any) => [styles.signInBtn, hovered && styles.signInBtnHover]}
                    accessibilityRole="button"
                    accessibilityLabel="התחברות"
                  >
                    <Text style={styles.signInText}>התחבר</Text>
                  </Pressable>
                )
              )}
              <Pressable
                onPress={() => navigate('/chat')}
                style={({ hovered, pressed }: any) => [
                  styles.ctaBtn,
                  hovered && styles.ctaBtnHover,
                  pressed && { opacity: 0.9 },
                ]}
                accessibilityRole="link"
                accessibilityLabel="התחל התאמה אישית"
              >
                <Text style={styles.ctaBtnText}>התחל התאמה ←</Text>
              </Pressable>
            </View>
          </View>
        </View>

        <AuthPopup visible={showAuthPopup} onClose={() => setShowAuthPopup(false)} returnTo="/" />
      </>
    );
  }

  // ─── Mobile ──────────────────────────────────────────────────────────────
  return (
    <>
      <View style={styles.mobRoot} accessibilityRole={'banner' as any}>
        <View style={styles.mobInner}>
          <Pressable
            onPress={() => setMenuOpen(v => !v)}
            style={styles.mobMenuBtn}
            accessibilityLabel={menuOpen ? 'סגור תפריט' : 'פתח תפריט'}
          >
            {menuOpen ? <X size={20} color={INK} /> : <Menu size={20} color={INK} />}
          </Pressable>

          <Pressable onPress={handleLogoPress} accessibilityRole="link" accessibilityLabel="Kursi">
            <Text style={styles.mobLogoText}>Kursi</Text>
          </Pressable>

          <Pressable
            onPress={() => navigate('/chat')}
            style={styles.mobCtaBtn}
            accessibilityRole="link"
            accessibilityLabel="התחל"
          >
            <Text style={styles.mobCtaBtnText}>התחל</Text>
          </Pressable>
        </View>
      </View>

      {menuOpen && (
        <View style={styles.mobDrawer} accessibilityRole={'navigation' as any}>
          {NAV_LINKS.map(({ label, href }) => (
            <Pressable
              key={href}
              onPress={() => navigate(href)}
              style={[styles.mobDrawerItem, isActive(href) && styles.mobDrawerItemActive]}
            >
              <Text style={[styles.mobDrawerLabel, isActive(href) && styles.mobDrawerLabelActive]}>
                {label}
              </Text>
            </Pressable>
          ))}
          <View style={{ height: 12 }} />
          {!authLoading && (
            authUser ? (
              <Pressable onPress={handleSignOut} style={styles.mobDrawerItem}>
                <Text style={[styles.mobDrawerLabel, { color: '#A8253A' }]}>התנתק</Text>
              </Pressable>
            ) : (
              <Pressable onPress={() => { setShowAuthPopup(true); setMenuOpen(false); }} style={styles.mobDrawerItem}>
                <Text style={styles.mobDrawerLabel}>התחבר / הרשם</Text>
              </Pressable>
            )
          )}
        </View>
      )}

      <AuthPopup visible={showAuthPopup} onClose={() => setShowAuthPopup(false)} returnTo="/" />
    </>
  );
}

const styles = StyleSheet.create({
  // ─── Desktop ────────────────────────────────────────────────────────────
  deskRoot: {
    backgroundColor: PAPER_SOFT,
    borderBottomWidth: 1,
    borderBottomColor: `${INK}12`,
    width: '100%',
    ...(IS_WEB ? { boxShadow: `0 1px 0 ${ACCENT}08` } as any : {}),
  },
  deskInner: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: 32,
    paddingVertical: 0,
    minHeight: 64,
  },
  logoBtn: {
    paddingVertical: 4,
    ...(IS_WEB ? { cursor: 'pointer' } as any : {}),
  },
  logoText: {
    fontFamily: SANS_BLACK,
    fontSize: 26,
    color: INK,
    letterSpacing: -0.8,
  },
  deskNav: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 4,
    flex: 1,
    justifyContent: 'center',
  } as any,
  deskNavLink: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
    position: 'relative',
    ...(IS_WEB ? { transition: 'background 150ms ease' } as any : {}),
  } as any,
  deskNavLinkHover: {
    backgroundColor: ACCENT_SOFT,
  },
  deskNavLabel: {
    fontFamily: SANS_MED,
    fontSize: 14,
    color: INK_SOFT,
    letterSpacing: 0.1,
  },
  deskNavLabelActive: {
    color: ACCENT,
    fontFamily: SANS_BOLD,
  },
  deskNavActiveDot: {
    position: 'absolute',
    bottom: 4,
    alignSelf: 'center',
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: ACCENT,
  },
  deskActions: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 10,
  } as any,
  authRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
  } as any,
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 99,
    borderWidth: 1.5,
    borderColor: `${ACCENT}40`,
    backgroundColor: ACCENT_SOFT,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontFamily: SANS_BOLD,
    fontSize: 13,
    color: ACCENT_DEEP,
  },
  signOutBtn: {
    padding: 4,
  },
  signInBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: `${INK}20`,
    ...(IS_WEB ? { transition: 'all 150ms ease' } as any : {}),
  },
  signInBtnHover: {
    backgroundColor: ACCENT_SOFT,
    borderColor: `${ACCENT}30`,
  },
  signInText: {
    fontFamily: SANS_MED,
    fontSize: 14,
    color: INK_SOFT,
  },
  ctaBtn: {
    paddingHorizontal: 18,
    paddingVertical: 9,
    borderRadius: 8,
    backgroundColor: ACCENT,
    ...(IS_WEB ? { transition: 'background 150ms ease', cursor: 'pointer' } as any : {}),
  },
  ctaBtnHover: {
    backgroundColor: ACCENT_DEEP,
  },
  ctaBtnText: {
    fontFamily: SANS_BOLD,
    fontSize: 14,
    color: PAPER_SOFT,
    letterSpacing: 0.1,
  },

  // ─── Mobile ─────────────────────────────────────────────────────────────
  mobRoot: {
    backgroundColor: PAPER_SOFT,
    borderBottomWidth: 1,
    borderBottomColor: `${INK}12`,
  },
  mobInner: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    minHeight: 56,
  },
  mobMenuBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: `${INK}15`,
    backgroundColor: PAPER,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobLogoText: {
    fontFamily: SANS_BLACK,
    fontSize: 22,
    color: INK,
    letterSpacing: -0.6,
  },
  mobCtaBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: ACCENT,
  },
  mobCtaBtnText: {
    fontFamily: SANS_BOLD,
    fontSize: 13,
    color: PAPER_SOFT,
  },
  mobDrawer: {
    backgroundColor: PAPER_SOFT,
    borderBottomWidth: 1,
    borderBottomColor: `${INK}12`,
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 16,
  },
  mobDrawerItem: {
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: `${INK}08`,
    borderStyle: 'solid' as any,
  } as any,
  mobDrawerItemActive: {
    borderBottomColor: `${ACCENT}20`,
  },
  mobDrawerLabel: {
    fontFamily: SANS_MED,
    fontSize: 17,
    color: INK_SOFT,
    textAlign: 'right',
  },
  mobDrawerLabelActive: {
    color: ACCENT,
    fontFamily: SANS_BOLD,
  },
});
